import bindAll from 'lodash.bindall';
import debounce from 'lodash.debounce';
import defaultsDeep from 'lodash.defaultsdeep';
import makeToolboxXML from '../lib/make-toolbox-xml';
import PropTypes from 'prop-types';
import React from 'react';
import VMScratchBlocks from '../lib/blocks';
import VM from 'scratch-vm';

import axios from 'axios';

import log from '../lib/log.js';
import Prompt from './prompt.jsx';
import BlocksComponent from '../components/blocks/blocks.jsx';
import BlockExportModal from '../components/block-export-modal/block-export-modal.jsx';
import ExtensionLibrary from './extension-library.jsx';
import extensionData from '../lib/libraries/extensions/index.jsx';
import CustomProcedures from './custom-procedures.jsx';
import errorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {BLOCKS_DEFAULT_SCALE, STAGE_DISPLAY_SIZES} from '../lib/layout-constants';
import DropAreaHOC from '../lib/drop-area-hoc.jsx';
import DragConstants from '../lib/drag-constants';
import defineDynamicBlock from '../lib/define-dynamic-block';
import {DEFAULT_THEME, getColorsForTheme, themeMap} from '../lib/themes';
import {injectExtensionBlockTheme, injectExtensionCategoryTheme} from '../lib/themes/blockHelpers';

import {connect} from 'react-redux';
import {updateToolbox} from '../reducers/toolbox';
import {activateColorPicker} from '../reducers/color-picker';
import {closeExtensionLibrary, openSoundRecorder, openConnectionModal} from '../reducers/modals';
import {activateCustomProcedures, deactivateCustomProcedures} from '../reducers/custom-procedures';
import {setConnectionModalExtensionId} from '../reducers/connection-modal';
import {updateMetrics} from '../reducers/workspace-metrics';
import {isTimeTravel2020} from '../reducers/time-travel';

import {
    activateTab,
    SOUNDS_TAB_INDEX
} from '../reducers/editor-tab';

import {changeMonskitGeneratedCodes} from '../reducers/generated-codes';
import downloadBlob from '../lib/download-blob';
import {exportBlockPngBlob, exportBlockSvgBlob} from '../lib/backpack/block-to-image';
import { writeServoLive } from '../lib/servo-live-transport';
import Dial from '../components/direction-picker/dial.jsx';
import servoDialStyles from './blocks-servo-dial.css';

const addFunctionListener = (object, property, callback) => {
    const oldFn = object[property];
    object[property] = function (...args) {
        const result = oldFn.apply(this, args);
        callback.apply(this, result);
        return result;
    };
};

const DroppableBlocks = DropAreaHOC([
    DragConstants.BACKPACK_CODE
])(BlocksComponent);

const SERVO_NUMERIC_BLOCK_TYPES = new Set([
    'math_number',
    'math_integer',
    'math_whole_number',
    'math_positive_number'
]);

class Blocks extends React.Component {
    constructor(props) {
        super(props);
        this.ScratchBlocks = VMScratchBlocks(props.vm, false);
        bindAll(this, [
            'attachVM',
            'debugProcedures',
            'getProcedureSnapshot',
            'detachVM',
            'getToolboxXML',
            'handleCategorySelected',
            'handleConnectionModalStart',
            'handleDrop',
            'handleStatusButtonUpdate',
            'handleOpenSoundRecorder',
            'handlePromptStart',
            'handlePromptCallback',
            'handlePromptClose',
            'handleCustomProceduresClose',
            'handleExportImageStart',
            'handleExportImageClose',
            'handleExportImageConfirm',
            'handleExportImageFormatChange',
            'onScriptGlowOn',
            'onScriptGlowOff',
            'onBlockGlowOn',
            'onBlockGlowOff',
            'handleMonitorsUpdate',
            'handleExtensionAdded',
            'handleBlocksInfoUpdate',
            'handleServoLiveChange',
            'attachServoLiveBlurHandler',
            'attachServoLiveFinishEditingHandler',
            'getInputBlockFieldValue',
            'getServoLiveTargetFromEventBlock',
            'sendServoLiveFromBlock',
            'openServoDialForBlock',
            'isEventFromFlyout',
            'handleServoDialMouseDown',
            'handleServoDialPopoverPointerDown',
            'handleServoDialOutsidePointerDown',
            'showServoDialClickFeedback',
            'clearServoDialClickFeedback',
            'updateServoDialPopover',
            'closeServoDialPopover',
            'handleServoDialChange',
            'handleServoDialDirectInput',
            'onTargetsUpdate',
            'onVisualReport',
            'onWorkspaceUpdate',
            'onWorkspaceMetricsChange',
            'releaseWorkspaceUpdateGuard',
            'setBlocks',
            'setLocale'
        ]);
        this.ScratchBlocks.prompt = this.handlePromptStart;
        this.ScratchBlocks.statusButtonCallback = this.handleConnectionModalStart;
        this.ScratchBlocks.recordSoundCallback = this.handleOpenSoundRecorder;
        this.ScratchBlocks.exportImageCallback = this.handleExportImageStart;

        this.state = {
            prompt: null,
            exportImage: null,
            servoDial: null,
            servoDialClickFeedback: null
        };
        this.isApplyingWorkspaceUpdate = false;
        this.ignoreBlockEventsUntil = 0;
        this.latestWorkspaceUpdateVersion = 0;
        this.workspaceUpdateGuardTimeout = null;
        this.onTargetsUpdate = debounce(this.onTargetsUpdate, 100);
        this.toolboxUpdateQueue = [];
        this.lastServoLiveMessage = null;
        this.pendingServoBlurListeners = new Set();
        this.pendingServoFallbackTimers = new Map();
        this.servoDialPopoverRef = null;
        this.servoDialClickFeedbackTimeoutId = null;
    }

    isAndroidAppWebView () {
      return typeof window !== 'undefined' &&
          typeof window.AndroidInterface !== 'undefined';
    }

    attachServoLiveBlurHandler (block, fieldName, sourceBlockId = block && block.id) {
      if (!block || !fieldName) return;

      const listenerKey = `${block.id}:${fieldName}:${sourceBlockId}`;
      if (this.pendingServoBlurListeners.has(listenerKey)) return;

      this.pendingServoBlurListeners.add(listenerKey);

      setTimeout(() => {
          const input = document.querySelector('.blocklyHtmlInput');

          if (!input) {
              this.pendingServoBlurListeners.delete(listenerKey);
              console.log('[servo-live-debug]', JSON.stringify({
                  source: 'blur-handler-missing-input',
                  blockId: block && block.id,
                  fieldName,
                  sourceBlockId
              }));
              this.scheduleServoLiveFallbackSend(block, fieldName, sourceBlockId, 'missing-input');
              return;
          }

          input.addEventListener('blur', async () => {
              this.pendingServoBlurListeners.delete(listenerKey);
              this.clearServoLiveFallbackSend(block, fieldName, sourceBlockId);
              console.log('[servo-live-debug]', JSON.stringify({
                  source: 'blur-fired',
                  blockId: block && block.id,
                  fieldName,
                  sourceBlockId
              }));
              await this.sendServoLiveFromBlock(block, fieldName);
          }, {once: true});
      }, 0);
    }

    attachServoLiveFinishEditingHandler (block, fieldName, sourceBlockId = block && block.id) {
      if (!block || !fieldName) return;

      const listenerKey = `${block.id}:${fieldName}:${sourceBlockId}:finish`;
      if (this.pendingServoBlurListeners.has(listenerKey)) return;

      const input = block.getInput && block.getInput(fieldName);
      const targetBlock = input && input.connection && input.connection.targetBlock ?
          input.connection.targetBlock() : null;
      if (!targetBlock || targetBlock.type !== 'math_number') return;

      const numberField = targetBlock.getField && targetBlock.getField('NUM');
      if (!numberField) return;

      const originalOnFinishEditing = numberField.onFinishEditing_;
      this.pendingServoBlurListeners.add(listenerKey);

      numberField.onFinishEditing_ = text => {
          this.pendingServoBlurListeners.delete(listenerKey);
          this.clearServoLiveFallbackSend(block, fieldName, sourceBlockId);
          numberField.onFinishEditing_ = originalOnFinishEditing;

          if (typeof originalOnFinishEditing === 'function') {
              originalOnFinishEditing.call(numberField, text);
          }

          console.log('[servo-live-debug]', JSON.stringify({
              source: 'finish-editing-fired',
              blockId: block && block.id,
              fieldName,
              sourceBlockId,
              text
          }));
          this.sendServoLiveFromBlock(block, fieldName);
      };
    }

    scheduleServoLiveFallbackSend (block, fieldName, sourceBlockId = block && block.id, reason = 'fallback') {
      if (!block || !fieldName) return;
      const timerKey = `${block.id}:${fieldName}:${sourceBlockId}:fallback`;
      this.clearServoLiveFallbackSend(block, fieldName, sourceBlockId);
      const timerId = setTimeout(() => {
          this.pendingServoFallbackTimers.delete(timerKey);
          console.log('[servo-live-debug]', JSON.stringify({
              source: 'fallback-fired',
              blockId: block && block.id,
              fieldName,
              sourceBlockId,
              reason
          }));
          this.sendServoLiveFromBlock(block, fieldName);
      }, 180);
      this.pendingServoFallbackTimers.set(timerKey, timerId);
    }

    clearServoLiveFallbackSend (block, fieldName, sourceBlockId = block && block.id) {
      if (!block || !fieldName) return;
      const timerKey = `${block.id}:${fieldName}:${sourceBlockId}:fallback`;
      const timerId = this.pendingServoFallbackTimers.get(timerKey);
      if (timerId) {
          clearTimeout(timerId);
          this.pendingServoFallbackTimers.delete(timerKey);
      }
    }

    isServoNumericBlock (block) {
        return Boolean(block && SERVO_NUMERIC_BLOCK_TYPES.has(block.type));
    }

    getInputBlockFieldValue (block, inputName) {
      if (!block || !block.getInput) return null;

      const input = block.getInput(inputName);
      if (!input || !input.connection) return null;

      const targetBlock = input.connection.targetBlock();
      if (!targetBlock) return null;

      if (this.isServoNumericBlock(targetBlock)) {
          return targetBlock.getFieldValue('NUM');
      }

      if (typeof targetBlock.getFieldValue === 'function') {
          return targetBlock.getFieldValue('NUM');
      }

      return null;
    }

    getServoLiveParentForNumberBlock (block) {
      if (!block) return null;

      const candidates = [];
      const surroundParent = block.getSurroundParent ? block.getSurroundParent() : null;
      if (surroundParent) candidates.push(surroundParent);
      if (block.parentBlock_ && candidates.indexOf(block.parentBlock_) === -1) {
          candidates.push(block.parentBlock_);
      }

      const sourceConnection = block.outputConnection;
      if (sourceConnection && sourceConnection.targetConnection &&
          sourceConnection.targetConnection.sourceBlock_) {
          const connectedParent = sourceConnection.targetConnection.sourceBlock_;
          if (candidates.indexOf(connectedParent) === -1) {
              candidates.push(connectedParent);
          }
      }

      for (const parentBlock of candidates) {
          if (!parentBlock) continue;

          if (parentBlock.type === 'xrobo_servo_2') {
              const arg2Block = parentBlock.getInput('ARG2') &&
                  parentBlock.getInput('ARG2').connection &&
                  parentBlock.getInput('ARG2').connection.targetBlock();
              if (arg2Block && arg2Block.id === block.id) {
                  return {block: parentBlock, fieldName: 'ARG2', useBlur: true, sourceBlockId: block.id};
              }
          }

          if (parentBlock.type === 'xrobo_servo_4') {
              const arg2Block = parentBlock.getInput('ARG2') &&
                  parentBlock.getInput('ARG2').connection &&
                  parentBlock.getInput('ARG2').connection.targetBlock();
              const arg3Block = parentBlock.getInput('ARG3') &&
                  parentBlock.getInput('ARG3').connection &&
                  parentBlock.getInput('ARG3').connection.targetBlock();

              if (arg2Block && arg2Block.id === block.id) {
                  return null;
              }

              if (arg3Block && arg3Block.id === block.id) {
                  return {block: parentBlock, fieldName: 'ARG3', useBlur: true, sourceBlockId: block.id};
              }
          }
      }

      return null;
    }

    getServoLiveTargetFromEventBlock (block, eventName) {
      if (!block) return null;

      if (block.type === 'xrobo_servo_1' && eventName === 'ARG2') {
          return {block, fieldName: 'ARG2', useBlur: false};
      }

      if (block.type === 'xrobo_servo_3' && eventName === 'ARG3') {
          return {block, fieldName: 'ARG3', useBlur: false};
      }

      if (this.isServoNumericBlock(block) && eventName === 'NUM') {
          return this.getServoLiveParentForNumberBlock(block);
      }

      return null;
    }

    getServoDialConfig (block) {
      if (!block) return null;

      if (block.type === 'xrobo_servo_1') {
          const angle = Number(block.getFieldValue('ARG2'));
          if (Number.isNaN(angle)) return null;
          return {fieldName: 'ARG2', angle};
      }

      if (block.type === 'xrobo_servo_2') {
          const angle = Number(this.getInputBlockFieldValue(block, 'ARG2'));
          if (Number.isNaN(angle)) return null;
          return {fieldName: 'ARG2', angle};
      }

      if (block.type === 'xrobo_servo_3') {
          const angle = Number(block.getFieldValue('ARG3'));
          if (Number.isNaN(angle)) return null;
          return {fieldName: 'ARG3', angle};
      }

      if (block.type === 'xrobo_servo_4') {
          const angle = Number(this.getInputBlockFieldValue(block, 'ARG3'));
          if (Number.isNaN(angle)) return null;
          return {fieldName: 'ARG3', angle};
      }

      return null;
    }

    getServoDialSelectionTarget (selectedBlock) {
      if (!selectedBlock) return null;

      if (this.isServoNumericBlock(selectedBlock)) {
          const parentTarget = this.getServoLiveParentForNumberBlock(selectedBlock);
          return parentTarget ? parentTarget.block : null;
      }

      return null;
    }

    getServoDialNumberField (block, fieldName) {
      if (!block || !fieldName) return null;
      const input = block.getInput && block.getInput(fieldName);
      const targetBlock = input && input.connection && input.connection.targetBlock();
      if (!this.isServoNumericBlock(targetBlock)) return null;
      return targetBlock.getField && targetBlock.getField('NUM');
    }

    openServoDialForBlock (block) {
      if (!block || typeof block.getSvgRoot !== 'function') {
          this.closeServoDialPopover();
          return;
      }

      const dialConfig = this.getServoDialConfig(block);
      if (!dialConfig) {
          this.closeServoDialPopover();
          return;
      }

      const svgRoot = block.getSvgRoot();
      if (!svgRoot) return;

      const rect = svgRoot.getBoundingClientRect();
      const viewportMargin = 16;
      const fallbackDialWidth = 224;
      const fallbackDialHeight = 240;
      const dialRect = this.servoDialPopoverRef &&
          this.servoDialPopoverRef.getBoundingClientRect &&
          this.servoDialPopoverRef.getBoundingClientRect();
      const dialWidth = dialRect && dialRect.width ? dialRect.width : fallbackDialWidth;
      const dialHeight = dialRect && dialRect.height ? dialRect.height : fallbackDialHeight;
      const maxLeft = Math.max(viewportMargin, window.innerWidth - dialWidth - viewportMargin);
      const maxTop = Math.max(viewportMargin, window.innerHeight - dialHeight - viewportMargin);
      const top = Math.min(
          maxTop,
          Math.max(viewportMargin, rect.top + (rect.height / 2) - (dialHeight / 2))
      );
      const left = Math.min(
          maxLeft,
          Math.max(viewportMargin, rect.right + 18)
      );

      this.setState(prevState => {
          const nextDial = {
              blockId: block.id,
              blockType: block.type,
              fieldName: dialConfig.fieldName,
              angle: dialConfig.angle,
              top,
              left
          };

          if (prevState.servoDial &&
              prevState.servoDial.blockId === nextDial.blockId &&
              prevState.servoDial.angle === nextDial.angle &&
              prevState.servoDial.top === nextDial.top &&
              prevState.servoDial.left === nextDial.left) {
              return null;
          }

          return {servoDial: nextDial};
      });
    }

    showServoDialClickFeedback (targetElement) {
      if (!targetElement || !targetElement.getBoundingClientRect) return;

      const rect = targetElement.getBoundingClientRect();
      if (!rect || (!rect.width && !rect.height)) return;

      const paddingX = 6;
      const paddingY = 4;
      clearTimeout(this.servoDialClickFeedbackTimeoutId);

      this.setState({
          servoDialClickFeedback: {
              top: Math.max(8, rect.top - paddingY),
              left: Math.max(8, rect.left - paddingX),
              width: Math.max(24, rect.width + (paddingX * 2)),
              height: Math.max(20, rect.height + (paddingY * 2))
          }
      });

      this.servoDialClickFeedbackTimeoutId = setTimeout(() => {
          this.clearServoDialClickFeedback();
      }, 280);
    }

    clearServoDialClickFeedback () {
      clearTimeout(this.servoDialClickFeedbackTimeoutId);
      this.servoDialClickFeedbackTimeoutId = null;
      if (this.state.servoDialClickFeedback) {
          this.setState({servoDialClickFeedback: null});
      }
    }

    isEventFromFlyout (event) {
      if (!this.workspace || !event || !event.target) return false;

      const flyout = this.workspace.getFlyout && this.workspace.getFlyout();
      const flyoutWorkspace = flyout && flyout.getWorkspace && flyout.getWorkspace();
      const flyoutSvg = flyoutWorkspace && flyoutWorkspace.getParentSvg && flyoutWorkspace.getParentSvg();

      if (flyoutSvg && flyoutSvg.contains(event.target)) {
          return true;
      }

      const eventTarget = event.target;
      return Boolean(
          eventTarget &&
          eventTarget.closest &&
          eventTarget.closest('.blocklyFlyout')
      );
    }

    handleServoDialMouseDown (event) {
      if (!this.workspace || !event || !event.target) return;
      if (this.isEventFromFlyout(event)) return;

      const blocks = this.workspace.getAllBlocks(false);
      for (const block of blocks) {
          if (!this.isServoNumericBlock(block)) continue;

          const numberField = block.getField && block.getField('NUM');
          const clickTarget = numberField && numberField.getClickTarget_ && numberField.getClickTarget_();
          const fieldSvgRoot = numberField && numberField.getSvgRoot && numberField.getSvgRoot();
          const hitTarget = (clickTarget && clickTarget.contains(event.target)) || (fieldSvgRoot && fieldSvgRoot.contains(event.target));

          if(!hitTarget) continue;

          const parentTarget = this.getServoLiveParentForNumberBlock(block);
          if (!parentTarget || !parentTarget.block) return;

          this.showServoDialClickFeedback(clickTarget || fieldSvgRoot);
          event.preventDefault();
          event.stopPropagation();
          this.openServoDialForBlock(parentTarget.block);
          return;
      }
    }

    handleServoDialPopoverPointerDown (event) {
        if (!event) return;
        event.stopPropagation();
    }

    handleServoDialOutsidePointerDown (event) {
      if (!this.state.servoDial || !event || !event.target) return;

      if (this.servoDialPopoverRef && this.servoDialPopoverRef.contains(event.target)) {
          return;
      }

      this.closeServoDialPopover();
    }
    
    updateServoDialPopover () {
      if (this.ScratchBlocks &&
          this.ScratchBlocks.WidgetDiv &&
          this.ScratchBlocks.WidgetDiv.isVisible()) {
          if (this.state.servoDial) {
              this.setState({servoDial: null});
          }
          return;
      }

      if (!this.workspace || !this.ScratchBlocks) {
          if (this.state.servoDial) {
              this.setState({servoDial: null});
          }
          return;
      }

      const selectedBlock = this.ScratchBlocks.selected;
      const dialTargetBlock = this.getServoDialSelectionTarget(selectedBlock);

      if (dialTargetBlock && typeof dialTargetBlock.getSvgRoot === 'function') {
          this.openServoDialForBlock(dialTargetBlock);
          return;
      }

      if (this.state.servoDial) {
          const existingBlock = this.workspace.getBlockById(this.state.servoDial.blockId);
          if (existingBlock && typeof existingBlock.getSvgRoot === 'function') {
              this.openServoDialForBlock(existingBlock);
              return;
          }
          this.setState({servoDial: null});
      }
    }

    closeServoDialPopover () {
      if (this.state.servoDial) {
          this.setState({servoDial: null});
      }
    }

    async handleServoDialChange (direction) {
      const {servoDial} = this.state;
      if (!servoDial || !this.workspace) return;

      const block = this.workspace.getBlockById(servoDial.blockId);
      if (!block) {
          this.closeServoDialPopover();
          return;
      }

      const nextAngle = Math.max(0, Math.min(180, Math.round(90 - direction)));

      if (block.type === 'xrobo_servo_1') {
          block.setFieldValue(String(nextAngle), 'ARG2');
      } else if (block.type === 'xrobo_servo_3') {
          block.setFieldValue(String(nextAngle), 'ARG3');
      } else if (block.type === 'xrobo_servo_2') {
          const input = block.getInput('ARG2');
          const targetBlock = input && input.connection && input.connection.targetBlock();
          if (this.isServoNumericBlock(targetBlock)) {
              targetBlock.setFieldValue(String(nextAngle), 'NUM');
          }
      } else if (block.type === 'xrobo_servo_4') {
          const input = block.getInput('ARG3');
          const targetBlock = input && input.connection && input.connection.targetBlock();
          if (this.isServoNumericBlock(targetBlock)) {
              targetBlock.setFieldValue(String(nextAngle), 'NUM');
          }
      }

      this.setState(prevState => ({
          servoDial: prevState.servoDial ? {
              ...prevState.servoDial,
              angle: nextAngle
          } : null
      }));

      await this.sendServoLiveFromBlock(block, servoDial.fieldName);
    }

    handleServoDialDirectInput () {
      const {servoDial} = this.state;
      if (!servoDial || !this.workspace) return;

      const block = this.workspace.getBlockById(servoDial.blockId);
      if (!block) {
          this.closeServoDialPopover();
          return;
      }

      const numberField = this.getServoDialNumberField(block, servoDial.fieldName);
      this.closeServoDialPopover();

      if (numberField && typeof numberField.showEditor_ === 'function') {
          setTimeout(() => {
              if (this.isAndroidAppWebView()) {
                  numberField.useTouchInteraction_ = true;
                  if (this.ScratchBlocks && this.ScratchBlocks.Touch) {
                      this.ScratchBlocks.Touch.clearTouchIdentifier();
                  }
              }
              numberField.showEditor_();
          }, 0);
      }
    }

    async sendServoLiveFromBlock (block, fieldName) {
      if (!this.props.servoLiveEnabled) {
          console.log('[servo-live-debug]', JSON.stringify({source: 'send-skipped-disabled'}));
          return;
      }
      if (!this.props.servoLiveConnected) {
          console.log('[servo-live-debug]', JSON.stringify({source: 'send-skipped-disconnected'}));
          return;
      }
      if (!block) {
          console.log('[servo-live-debug]', JSON.stringify({source: 'send-skipped-no-block'}));
          return;
      }

      const rawServoIndex = block.getFieldValue('ARG1');
      const xroboServoPortMap = {
          OUT1: '2',
          OUT2: '3',
          OUT3: '4',
          OUT4: '5',
          OUT5: '6',
          OUT6: '7',
          OUT7: '0',
          OUT8: '1'
      };
      const servoIndex = xroboServoPortMap[rawServoIndex] || rawServoIndex;

      let angle;
      if (block.type === 'xrobo_servo_1' && fieldName === 'ARG2') {
          angle = block.getFieldValue('ARG2');
      } else if (block.type === 'xrobo_servo_3' && fieldName === 'ARG3') {
          angle = block.getFieldValue('ARG3');
      } else if (block.type === 'xrobo_servo_2' && fieldName === 'ARG2') {
          angle = this.getInputBlockFieldValue(block, 'ARG2');
      } else if (block.type === 'xrobo_servo_4' && fieldName === 'ARG3') {
          angle = this.getInputBlockFieldValue(block, 'ARG3');
      } else {
          return;
      }

      if (servoIndex === null || typeof angle === 'undefined' || angle === '') {
          console.log('[servo-live-debug]', JSON.stringify({
              source: 'send-skipped-invalid-angle',
              blockId: block.id,
              blockType: block.type,
              fieldName,
              rawServoIndex,
              servoIndex,
              angle
          }));
          return;
      }

      const message = `S${servoIndex},${angle},6`;

      if (this.lastServoLiveMessage === message) {
          console.log('[servo-live-debug]', JSON.stringify({
              source: 'send-skipped-duplicate',
              blockId: block.id,
              blockType: block.type,
              fieldName,
              message
          }));
          return;
      }
      this.lastServoLiveMessage = message;

      try {
          console.log('[servo-live-debug]', JSON.stringify({
              source: 'send-attempt',
              blockId: block.id,
              blockType: block.type,
              fieldName,
              message
          }));
          await writeServoLive(message);
          console.log('Servo live message sent:', message);
      } catch (error) {
          console.error('Failed to send servo live message:', error);
      }
    }

    async handleServoLiveChange (event) {
      if (!this.props.servoLiveEnabled) {
          return;
      }
      if (!this.props.servoLiveConnected) {
          return;
      }
      if (!event || event.type !== 'change') {
          return;
      }
      if (event.element !== 'field') {
          return;
      }

      console.log('[servo-live-debug]', JSON.stringify({
          source: 'change-event',
          eventType: event.type,
          blockId: event.blockId || null,
          name: event.name || null,
          element: event.element || null
      }));

      const block = this.workspace && event.blockId ? this.workspace.getBlockById(event.blockId) : null;
      if (!block) return;

      const target = this.getServoLiveTargetFromEventBlock(block, event.name);
      if (!target) {
          console.log('[servo-live-debug]', JSON.stringify({
              source: 'change-event-no-target',
              blockId: block.id,
              blockType: block.type,
              eventName: event.name
          }));
          return;
      }

      if (target.useBlur) {
          console.log('[servo-live-debug]', JSON.stringify({
              source: 'change-event-use-blur',
              blockId: target.block && target.block.id,
              blockType: target.block && target.block.type,
              fieldName: target.fieldName,
              sourceBlockId: target.sourceBlockId || null
          }));
          this.attachServoLiveBlurHandler(target.block, target.fieldName, target.sourceBlockId);
          this.attachServoLiveFinishEditingHandler(
              target.block,
              target.fieldName,
              target.sourceBlockId
          );
          return;
      }

      await this.sendServoLiveFromBlock(target.block, target.fieldName);
    }


    getProcedureSnapshot() {
        if (!this.workspace) {
            return {
                definitions: [],
                prototypes: [],
                calls: []
            };
        }
        const blocks = this.workspace.getAllBlocks(false);
        const definitions = [];
        const prototypes = [];
        const calls = [];
        for (const block of blocks) {
            if (block.type === 'procedures_definition') {
                const input = block.getInput && block.getInput('custom_block');
                const prototypeBlock = input && input.connection && input.connection.targetBlock ?
                    input.connection.targetBlock() : null;
                definitions.push({
                    id: block.id,
                    procCode: prototypeBlock && prototypeBlock.getProcCode ? prototypeBlock.getProcCode() : null
                });
            } else if (block.type === 'procedures_prototype') {
                prototypes.push({
                    id: block.id,
                    procCode: block.getProcCode ? block.getProcCode() : null,
                    parent: block.parentBlock_ ? block.parentBlock_.id : null
                });
            } else if (block.type === 'procedures_call') {
                calls.push({
                    id: block.id,
                    procCode: block.getProcCode ? block.getProcCode() : null
                });
            }
        }
        return {definitions, prototypes, calls};
    }

    debugProcedures(label, extra = {}) {
        const snapshot = this.getProcedureSnapshot();
        const payload = {
            label,
            href: typeof window !== 'undefined' ? window.location.href : '',
            definitions: snapshot.definitions,
            prototypes: snapshot.prototypes,
            calls: snapshot.calls,
            ...extra
        };
        const serialized = JSON.stringify(payload);
        console.log(`[proc-debug] ${serialized}`);
        if (typeof window !== 'undefined') {
            window.__procDebug = payload;
        }
    }

    componentDidMount() {
        this.ScratchBlocks = VMScratchBlocks(this.props.vm, this.props.useCatBlocks);
        this.ScratchBlocks.prompt = this.handlePromptStart;
        this.ScratchBlocks.statusButtonCallback = this.handleConnectionModalStart;
        this.ScratchBlocks.recordSoundCallback = this.handleOpenSoundRecorder;
        this.ScratchBlocks.exportImageCallback = this.handleExportImageStart;

        this.ScratchBlocks.FieldColourSlider.activateEyedropper_ = this.props.onActivateColorPicker;
        this.ScratchBlocks.Procedures.externalProcedureDefCallback = this.props.onActivateCustomProcedures;
        this.ScratchBlocks.ScratchMsgs.setLocale(this.props.locale);

        const workspaceConfig = defaultsDeep({},
            Blocks.defaultOptions,
            this.props.options,
            {rtl: this.props.isRtl, toolbox: this.props.toolboxXML, colours: getColorsForTheme(this.props.theme)}
        );
        this.workspace = this.ScratchBlocks.inject(this.blocks, workspaceConfig);
        window.currentBlocklyWorkspace = this.workspace;
        this.debugProcedures('componentDidMount:afterInject');


        // Register buttons under new callback keys for creating variables,
        // lists, and procedures from extensions.

        const varListButtonCallback = type =>
            (() => this.ScratchBlocks.Variables.createVariable(this.workspace, null, type));
        const procButtonCallback = () => {
            this.ScratchBlocks.Procedures.createProcedureDefCallback_(this.workspace);
        };

        // Flyout buttons resolve callbacks from the target workspace, not the flyout workspace.
        this.workspace.registerButtonCallback('MAKE_A_VARIABLE', varListButtonCallback(''));
        this.workspace.registerButtonCallback('MAKE_A_LIST', varListButtonCallback('list'));
        this.workspace.registerButtonCallback('MAKE_A_PROCEDURE', procButtonCallback);
        this.debugWorkspaceListener = event => {
            if (!event || !event.type) return;
            if (
                event.type === 'create' ||
                event.type === 'delete' ||
                event.type === 'change' ||
                event.type === 'move'
            ) {
                this.debugProcedures(`workspace-event:${event.type}`, {
                    eventBlockId: event.blockId || null,
                    eventElement: event.element || null
                });
            }
        };
        this.workspace.addChangeListener(this.debugWorkspaceListener);
        if (this.workspace && this.workspace.getParentSvg) {
            this.workspaceMouseDownTarget = this.workspace.getParentSvg();
            if (this.workspaceMouseDownTarget) {
                this.workspaceMouseDownTarget.addEventListener('mousedown', this.handleServoDialMouseDown, true);
                this.workspaceMouseDownTarget.addEventListener('touchstart', this.handleServoDialMouseDown, true);
            }
        }
        document.addEventListener('mousedown', this.handleServoDialOutsidePointerDown);
        document.addEventListener('touchstart', this.handleServoDialOutsidePointerDown);

        // Store the xml of the toolbox that is actually rendered.
        // This is used in componentDidUpdate instead of prevProps, because
        // the xml can change while e.g. on the costumes tab.
        this._renderedToolboxXML = this.props.toolboxXML;

        // Keep a handle to the original toolbox refresh toggle.
        this.setToolboxRefreshEnabled = this.workspace.setToolboxRefreshEnabled.bind(this.workspace);
        this.workspace.setToolboxRefreshEnabled = enabled => {
            this.setToolboxRefreshEnabled(enabled);
        };

        // @todo change this when blockly supports UI events
        addFunctionListener(this.workspace, 'translate', this.onWorkspaceMetricsChange);
        addFunctionListener(this.workspace, 'zoom', this.onWorkspaceMetricsChange);

        this.attachVM();
        // Only update blocks/vm locale when visible to avoid sizing issues
        // If locale changes while not visible it will get handled in didUpdate
        if (this.props.isVisible) {
            this.setLocale();
        }
        const this_=this;
        const workspaceUpdateByMenuChangeHandler = (event) => {
            let output = '';
            try {
                output = Blockly.Arduino.workspaceToCode(this_.workspace);
            } catch (err) {
                ;
            }
            this_.props.onchangeMonskitGeneratedCodes(output);
        };

        window.addEventListener('workspaceUpdateByMenuChange', workspaceUpdateByMenuChangeHandler);
        window.workspaceUpdateByMenuChangeHandler = workspaceUpdateByMenuChangeHandler;
    }

    shouldComponentUpdate(nextProps, nextState) {
        let output = '';
        try {
            output = Blockly.Arduino.workspaceToCode(this.workspace);
        } catch (err) {
            ;
        }
        this.props.onchangeMonskitGeneratedCodes(output);
        return (
            this.state.prompt !== nextState.prompt ||
            this.state.exportImage !== nextState.exportImage ||
            this.state.servoDial !== nextState.servoDial ||
            this.props.isVisible !== nextProps.isVisible ||
            this._renderedToolboxXML !== nextProps.toolboxXML ||
            this.props.extensionLibraryVisible !== nextProps.extensionLibraryVisible ||
            this.props.customProceduresVisible !== nextProps.customProceduresVisible ||
            this.props.locale !== nextProps.locale ||
            this.props.anyModalVisible !== nextProps.anyModalVisible ||
            this.props.stageSize !== nextProps.stageSize
        );
    }

    componentDidUpdate(prevProps) {
        if (this.props.customProceduresVisible !== prevProps.customProceduresVisible) {
            this.debugProcedures('componentDidUpdate:customProceduresVisible', {
                visible: this.props.customProceduresVisible
            });
        }
        // If any modals are open, call hideChaff to close z-indexed field editors
        if (this.props.anyModalVisible && !prevProps.anyModalVisible) {
            this.ScratchBlocks.hideChaff();
        }

        // Only rerender the toolbox when the blocks are visible and the xml is
        // different from the previously rendered toolbox xml.
        // Do not check against prevProps.toolboxXML because that may not have been rendered.
        if (this.props.isVisible && this.props.toolboxXML !== this._renderedToolboxXML) {
            this.requestToolboxUpdate();
        }

        if (this.props.isVisible === prevProps.isVisible) {
            if (this.props.stageSize !== prevProps.stageSize) {
                // force workspace to redraw for the new stage size
                window.dispatchEvent(new Event('resize'));
            }
            return;
        }
        // @todo hack to resize blockly manually in case resize happened while hidden
        // @todo hack to reload the workspace due to gui bug #413
        if (this.props.isVisible) { // Scripts tab
            this.workspace.setVisible(true);
            if (prevProps.locale !== this.props.locale || this.props.locale !== this.props.vm.getLocale()) {
                // call setLocale if the locale has changed, or changed while the blocks were hidden.
                // vm.getLocale() will be out of sync if locale was changed while not visible
                this.setLocale();
            } else {
                this.props.vm.refreshWorkspace();
                this.requestToolboxUpdate();
            }

            window.dispatchEvent(new Event('resize'));
        } else {
            this.workspace.setVisible(false);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.workspaceUpdateGuardTimeout);
        this.detachVM();
        if (this.debugWorkspaceListener) {
            this.workspace.removeChangeListener(this.debugWorkspaceListener);
        }
        if (this.workspaceMouseDownTarget) {
            this.workspaceMouseDownTarget.removeEventListener('mousedown', this.handleServoDialMouseDown, true);
            this.workspaceMouseDownTarget.removeEventListener('touchstart', this.handleServoDialMouseDown, true);
            this.workspaceMouseDownTarget = null;
        }
        document.removeEventListener('mousedown', this.handleServoDialOutsidePointerDown);
        document.removeEventListener('touchstart', this.handleServoDialOutsidePointerDown);
        clearTimeout(this.servoDialClickFeedbackTimeoutId);
        this.servoDialClickFeedbackTimeoutId = null;
        this.workspace.dispose();
        clearTimeout(this.toolboxUpdateTimeout);

        // Clear the flyout blocks so that they can be recreated on mount.
        this.props.vm.clearFlyoutBlocks();
        if (window.workspaceUpdateByMenuChangeHandler) {
            window.removeEventListener('workspaceUpdateByMenuChange', window.workspaceUpdateByMenuChangeHandler);
            delete window.workspaceUpdateByMenuChangeHandler; // Clean up reference
        }
        if (window.currentBlocklyWorkspace === this.workspace) {
            delete window.currentBlocklyWorkspace;
        }

    }

    requestToolboxUpdate() {
        clearTimeout(this.toolboxUpdateTimeout);
        this.toolboxUpdateTimeout = setTimeout(() => {
            this.updateToolbox();
        }, 0);
    }

    setLocale() {
        this.ScratchBlocks.ScratchMsgs.setLocale(this.props.locale);
        this.props.vm.setLocale(this.props.locale, this.props.messages)
            .then(() => {
                this.workspace.getFlyout().setRecyclingEnabled(false);
                this.props.vm.refreshWorkspace();
                this.requestToolboxUpdate();
                this.withToolboxUpdates(() => {
                    this.workspace.getFlyout().setRecyclingEnabled(true);
                });
            });
    }

    updateToolbox() {
        this.toolboxUpdateTimeout = false;

        const categoryId = this.workspace.toolbox_.getSelectedCategoryId();
        const offset = this.workspace.toolbox_.getCategoryScrollOffset();
        this.workspace.updateToolbox(this.props.toolboxXML);
        this._renderedToolboxXML = this.props.toolboxXML;

        // In order to catch any changes that mutate the toolbox during "normal runtime"
        // (variable changes/etc), re-enable toolbox refresh.
        // Using the setter function will rerender the entire toolbox which we just rendered.
        this.workspace.toolboxRefreshEnabled_ = true;

        const currentCategoryPos = this.workspace.toolbox_.getCategoryPositionById(categoryId);
        const currentCategoryLen = this.workspace.toolbox_.getCategoryLengthById(categoryId);
        if (offset < currentCategoryLen) {
            this.workspace.toolbox_.setFlyoutScrollPos(currentCategoryPos + offset);
        } else {
            this.workspace.toolbox_.setFlyoutScrollPos(currentCategoryPos);
        }

        this.toolboxUpdateTimeout = false;

        const queue = this.toolboxUpdateQueue;
        this.toolboxUpdateQueue = [];
        queue.forEach(fn => fn());
    }

    withToolboxUpdates(fn) {
        // if there is a queued toolbox update, we need to wait
        if (this.toolboxUpdateTimeout) {
            this.toolboxUpdateQueue.push(fn);
        } else {
            fn();
        }
    }

    releaseWorkspaceUpdateGuard() {
        clearTimeout(this.workspaceUpdateGuardTimeout);
        this.workspaceUpdateGuardTimeout = null;
        this.isApplyingWorkspaceUpdate = false;
        if (this.workspace && this.blockListenerProxy) {
            this.workspace.addChangeListener(this.blockListenerProxy);
        }
    }

    attachVM() {
        this.props.vm.setWorkspaceProvider(() =>
            this.ScratchBlocks.Xml.workspaceToDom(this.workspace)
        );
        this.blockListenerProxy = event => {
            if (this.isApplyingWorkspaceUpdate || Date.now() < this.ignoreBlockEventsUntil) {
                return;
            }
            this.handleServoLiveChange(event);
            this.updateServoDialPopover();
            this.props.vm.blockListener(event);
        };
        this.workspace.addChangeListener(this.blockListenerProxy);
        this.flyoutWorkspace = this.workspace
            .getFlyout()
            .getWorkspace();
        this.flyoutWorkspace.addChangeListener(this.props.vm.flyoutBlockListener);
        this.flyoutWorkspace.addChangeListener(this.props.vm.monitorBlockListener);
        this.props.vm.addListener('SCRIPT_GLOW_ON', this.onScriptGlowOn);
        this.props.vm.addListener('SCRIPT_GLOW_OFF', this.onScriptGlowOff);
        this.props.vm.addListener('BLOCK_GLOW_ON', this.onBlockGlowOn);
        this.props.vm.addListener('BLOCK_GLOW_OFF', this.onBlockGlowOff);
        this.props.vm.addListener('VISUAL_REPORT', this.onVisualReport);
        this.props.vm.addListener('workspaceUpdate', this.onWorkspaceUpdate);
        this.props.vm.addListener('targetsUpdate', this.onTargetsUpdate);
        this.props.vm.addListener('MONITORS_UPDATE', this.handleMonitorsUpdate);
        this.props.vm.addListener('EXTENSION_ADDED', this.handleExtensionAdded);
        this.props.vm.addListener('BLOCKSINFO_UPDATE', this.handleBlocksInfoUpdate);
        this.props.vm.addListener('PERIPHERAL_CONNECTED', this.handleStatusButtonUpdate);
        this.props.vm.addListener('PERIPHERAL_DISCONNECTED', this.handleStatusButtonUpdate);
    }

    detachVM() {
        this.props.vm.setWorkspaceProvider(null);
        this.props.vm.removeListener('SCRIPT_GLOW_ON', this.onScriptGlowOn);
        this.props.vm.removeListener('SCRIPT_GLOW_OFF', this.onScriptGlowOff);
        this.props.vm.removeListener('BLOCK_GLOW_ON', this.onBlockGlowOn);
        this.props.vm.removeListener('BLOCK_GLOW_OFF', this.onBlockGlowOff);
        this.props.vm.removeListener('VISUAL_REPORT', this.onVisualReport);
        this.props.vm.removeListener('workspaceUpdate', this.onWorkspaceUpdate);
        this.props.vm.removeListener('targetsUpdate', this.onTargetsUpdate);
        this.props.vm.removeListener('MONITORS_UPDATE', this.handleMonitorsUpdate);
        this.props.vm.removeListener('EXTENSION_ADDED', this.handleExtensionAdded);
        this.props.vm.removeListener('BLOCKSINFO_UPDATE', this.handleBlocksInfoUpdate);
        this.props.vm.removeListener('PERIPHERAL_CONNECTED', this.handleStatusButtonUpdate);
        this.props.vm.removeListener('PERIPHERAL_DISCONNECTED', this.handleStatusButtonUpdate);
    }

    updateToolboxBlockValue(id, value) {
        this.withToolboxUpdates(() => {
            const block = this.workspace
                .getFlyout()
                .getWorkspace()
                .getBlockById(id);
            if (block) {
                block.inputList[0].fieldRow[0].setValue(value);
            }
        });
    }

    onTargetsUpdate() {
        if (this.props.vm.editingTarget && this.workspace.getFlyout()) {
            ['glide', 'move', 'set'].forEach(prefix => {
                this.updateToolboxBlockValue(`${prefix}x`, Math.round(this.props.vm.editingTarget.x).toString());
                this.updateToolboxBlockValue(`${prefix}y`, Math.round(this.props.vm.editingTarget.y).toString());
            });
        }
    }

    onWorkspaceMetricsChange() {
        const target = this.props.vm.editingTarget;
        if (target && target.id) {
            // Dispatch updateMetrics later, since onWorkspaceMetricsChange may be (very indirectly)
            // called from a reducer, i.e. when you create a custom procedure.
            // TODO: Is this a vehement hack?
            setTimeout(() => {
                this.props.updateMetrics({
                    targetID: target.id,
                    scrollX: this.workspace.scrollX,
                    scrollY: this.workspace.scrollY,
                    scale: this.workspace.scale
                });
            }, 0);
        }
    }

    onScriptGlowOn(data) {
        this.workspace.glowStack(data.id, true);
    }

    onScriptGlowOff(data) {
        this.workspace.glowStack(data.id, false);
    }

    onBlockGlowOn(data) {
        this.workspace.glowBlock(data.id, true);
    }

    onBlockGlowOff(data) {
        this.workspace.glowBlock(data.id, false);
    }

    onVisualReport(data) {
        this.workspace.reportValue(data.id, data.value);
    }

    getToolboxXML() {
        // Use try/catch because this requires digging pretty deep into the VM
        // Code inside intentionally ignores several error situations (no stage, etc.)
        // Because they would get caught by this try/catch
        try {
            let {editingTarget: target, runtime} = this.props.vm;
            const stage = runtime.getTargetForStage();
            if (!target) target = stage; // If no editingTarget, use the stage

            const stageCostumes = stage.getCostumes();
            const targetCostumes = target.getCostumes();
            const targetSounds = target.getSounds();
            const dynamicBlocksXML = injectExtensionCategoryTheme(
                this.props.vm.runtime.getBlocksXML(target),
                this.props.theme
            );

            /////// 필수코드 아닐수도 있음. xrobo ////////
            let index = dynamicBlocksXML.findIndex(p => p.id === 'xrobo');
            if (index > -1) {
                dynamicBlocksXML.splice(index, 1);
            }
            ///////////////
            return makeToolboxXML(false, target.isStage, target.id, dynamicBlocksXML,
                targetCostumes[targetCostumes.length - 1].name,
                stageCostumes[stageCostumes.length - 1].name,
                targetSounds.length > 0 ? targetSounds[targetSounds.length - 1].name : '',
                getColorsForTheme(this.props.theme),
                this.props.locale
            );
        } catch {
            return null;
        }
    }

    onWorkspaceUpdate(data) {
        const updateVersion = data && typeof data.version === 'number' ? data.version : 0;
        if (updateVersion && updateVersion < this.latestWorkspaceUpdateVersion) {
            this.debugProcedures('onWorkspaceUpdate:ignoredStale', {
                version: updateVersion,
                latestVersion: this.latestWorkspaceUpdateVersion,
                xmlLength: data && data.xml ? data.xml.length : 0
            });
            return;
        }
        if (updateVersion) {
            this.latestWorkspaceUpdateVersion = updateVersion;
        }
        this.debugProcedures('onWorkspaceUpdate:beforeLoad', {
            xmlLength: data && data.xml ? data.xml.length : 0,
            version: updateVersion
        });
        clearTimeout(this.workspaceUpdateGuardTimeout);
        this.isApplyingWorkspaceUpdate = true;
        this.ignoreBlockEventsUntil = Date.now() + 2000;
        // When we change sprites, update the toolbox to have the new sprite's blocks
        const toolboxXML = this.getToolboxXML();
        if (toolboxXML) {
            this.props.updateToolboxState(toolboxXML);
        }

        if (this.props.vm.editingTarget && !this.props.workspaceMetrics.targets[this.props.vm.editingTarget.id]) {
            this.onWorkspaceMetricsChange();
        }

        // Remove and reattach the workspace listener (but allow flyout events)
        this.workspace.removeChangeListener(this.blockListenerProxy);
        const dom = this.ScratchBlocks.Xml.textToDom(data.xml);
        try {
            this.ScratchBlocks.Xml.clearWorkspaceAndLoadFromXml(dom, this.workspace);
        } catch (error) {
            // The workspace is likely incomplete. What did update should be
            // functional.
            //
            // Instead of throwing the error, by logging it and continuing as
            // normal lets the other workspace update processes complete in the
            // gui and vm, which lets the vm run even if the workspace is
            // incomplete. Throwing the error would keep things like setting the
            // correct editing target from happening which can interfere with
            // some blocks and processes in the vm.
            if (error.message) {
                error.message = `Workspace Update Error: ${error.message}`;
            }
            log.error(error);
        }
        this.workspaceUpdateGuardTimeout = setTimeout(() => {
            this.releaseWorkspaceUpdateGuard();
        }, 1000);
        this.updateServoDialPopover();
        this.debugProcedures('onWorkspaceUpdate:afterLoad', {
            version: updateVersion
        });

        if (this.props.vm.editingTarget && this.props.workspaceMetrics.targets[this.props.vm.editingTarget.id]) {
            const {scrollX, scrollY, scale} = this.props.workspaceMetrics.targets[this.props.vm.editingTarget.id];
            this.workspace.scrollX = scrollX;
            this.workspace.scrollY = scrollY;
            this.workspace.scale = scale;
            this.workspace.resize();
        }

        // Clear the undo state of the workspace since this is a
        // fresh workspace and we don't want any changes made to another sprites
        // workspace to be 'undone' here.
        this.workspace.clearUndo();
    }

    handleMonitorsUpdate(monitors) {
        // Update the checkboxes of the relevant monitors.
        // TODO: What about monitors that have fields? See todo in scratch-vm blocks.js changeBlock:
        // https://github.com/LLK/scratch-vm/blob/2373f9483edaf705f11d62662f7bb2a57fbb5e28/src/engine/blocks.js#L569-L576
        const flyout = this.workspace.getFlyout();
        for (const monitor of monitors.values()) {
            const blockId = monitor.get('id');
            const isVisible = monitor.get('visible');
            flyout.setCheckboxState(blockId, isVisible);
            // We also need to update the isMonitored flag for this block on the VM, since it's used to determine
            // whether the checkbox is activated or not when the checkbox is re-displayed (e.g. local variables/blocks
            // when switching between sprites).
            const block = this.props.vm.runtime.monitorBlocks.getBlock(blockId);
            if (block) {
                block.isMonitored = isVisible;
            }
        }
    }

    handleExtensionAdded(categoryInfo) {
        // xrobo, 필수 아닐수도있음
        if (categoryInfo.id === 'monskit') {
            console.log('what does it do?')
            return;
        }
        const defineBlocks = blockInfoArray => {
            if (blockInfoArray && blockInfoArray.length > 0) {
                const staticBlocksJson = [];
                const dynamicBlocksInfo = [];
                blockInfoArray.forEach(blockInfo => {
                    if (blockInfo.info && blockInfo.info.isDynamic) {
                        dynamicBlocksInfo.push(blockInfo);
                    } else if (blockInfo.json) {
                        staticBlocksJson.push(injectExtensionBlockTheme(blockInfo.json, this.props.theme));
                    }
                    // otherwise it's a non-block entry such as '---'
                });

                this.ScratchBlocks.defineBlocksWithJsonArray(staticBlocksJson);
                dynamicBlocksInfo.forEach(blockInfo => {
                    // This is creating the block factory / constructor -- NOT a specific instance of the block.
                    // The factory should only know static info about the block: the category info and the opcode.
                    // Anything else will be picked up from the XML attached to the block instance.
                    const extendedOpcode = `${categoryInfo.id}_${blockInfo.info.opcode}`;
                    const blockDefinition =
                        defineDynamicBlock(this.ScratchBlocks, categoryInfo, blockInfo, extendedOpcode);
                    this.ScratchBlocks.Blocks[extendedOpcode] = blockDefinition;
                });
            }
        };

        // scratch-blocks implements a menu or custom field as a special kind of block ("shadow" block)
        // these actually define blocks and MUST run regardless of the UI state
        defineBlocks(
            Object.getOwnPropertyNames(categoryInfo.customFieldTypes)
                .map(fieldTypeName => categoryInfo.customFieldTypes[fieldTypeName].scratchBlocksDefinition));
        defineBlocks(categoryInfo.menus);
        defineBlocks(categoryInfo.blocks);

        // Update the toolbox with new blocks if possible
        const toolboxXML = this.getToolboxXML();
        if (toolboxXML) {
            this.props.updateToolboxState(toolboxXML);
        }
    }

    handleBlocksInfoUpdate(categoryInfo) {
        // @todo Later we should replace this to avoid all the warnings from redefining blocks.
        this.handleExtensionAdded(categoryInfo);
    }

    handleCategorySelected(categoryId) {
        const extension = extensionData.find(ext => ext.extensionId === categoryId);
        if (extension && extension.launchPeripheralConnectionFlow) {
            this.handleConnectionModalStart(categoryId);
        }

        this.withToolboxUpdates(() => {
            this.workspace.toolbox_.setSelectedCategoryById(categoryId);
        });
    }

    setBlocks(blocks) {
        this.blocks = blocks;
    }

    handlePromptStart(message, defaultValue, callback, optTitle, optVarType) {
        const p = {prompt: {callback, message, defaultValue}};
        p.prompt.title = optTitle ? optTitle :
            this.ScratchBlocks.Msg.VARIABLE_MODAL_TITLE;
        p.prompt.varType = typeof optVarType === 'string' ?
            optVarType : this.ScratchBlocks.SCALAR_VARIABLE_TYPE;
        p.prompt.showVariableOptions = // This flag means that we should show variable/list options about scope
            optVarType !== this.ScratchBlocks.BROADCAST_MESSAGE_VARIABLE_TYPE &&
            p.prompt.title !== this.ScratchBlocks.Msg.RENAME_VARIABLE_MODAL_TITLE &&
            p.prompt.title !== this.ScratchBlocks.Msg.RENAME_LIST_MODAL_TITLE;
        p.prompt.showCloudOption = (optVarType === this.ScratchBlocks.SCALAR_VARIABLE_TYPE) && this.props.canUseCloud;
        this.setState(p);
    }

    handleConnectionModalStart(extensionId) {
        this.props.onOpenConnectionModal(extensionId);
    }

    handleStatusButtonUpdate() {
        this.ScratchBlocks.refreshStatusButtons(this.workspace);
    }

    handleOpenSoundRecorder() {
        this.props.onOpenSoundRecorder();
    }

    handleExportImageStart(blockId) {
        this.setState({
            exportImage: {
                blockId,
                format: 'png'
            }
        });
    }

    handleExportImageClose() {
        this.setState({exportImage: null});
    }

    handleExportImageFormatChange(event) {
        const {value} = event.target;
        this.setState(prevState => ({
            exportImage: {
                ...prevState.exportImage,
                format: value
            }
        }));
    }

    async handleExportImageConfirm() {
        const {blockId, format} = this.state.exportImage;
        try {
            const blob = await (format === 'svg' ?
                exportBlockSvgBlob(blockId) :
                exportBlockPngBlob(blockId));
            downloadBlob(`block-image.${format}`, blob);
            this.handleExportImageClose();
        } catch (error) {
            log.error('Could not export block image', error);
        }
    }

    /*
     * Pass along information about proposed name and variable options (scope and isCloud)
     * and additional potentially conflicting variable names from the VM
     * to the variable validation prompt callback used in scratch-blocks.
     */
    handlePromptCallback(input, variableOptions) {
        this.state.prompt.callback(
            input,
            this.props.vm.runtime.getAllVarNamesOfType(this.state.prompt.varType),
            variableOptions);
        this.handlePromptClose();
    }

    handlePromptClose() {
        this.setState({prompt: null});
    }

    handleCustomProceduresClose(data) {
        this.debugProcedures('handleCustomProceduresClose:before', {
            hasMutation: Boolean(data)
        });
        this.props.onRequestCloseCustomProcedures(data);
        const ws = this.workspace;
        ws.refreshToolboxSelection_();
        ws.toolbox_.scrollToCategoryById('myBlocks');
        this.debugProcedures('handleCustomProceduresClose:after', {
            hasMutation: Boolean(data)
        });
    }

    handleDrop(dragInfo) {
        fetch(dragInfo.payload.bodyUrl)
            .then(response => response.json())
            .then(blocks => this.props.vm.shareBlocksToTarget(blocks, this.props.vm.editingTarget.id))
            .then(() => {
                this.props.vm.refreshWorkspace();
                this.updateToolbox(); // To show new variables/custom blocks
            });
    }

    isAndroidAppWe

    render() {
        /* eslint-disable no-unused-vars */
        const {
            anyModalVisible,
            canUseCloud,
            customProceduresVisible,
            extensionLibraryVisible,
            options,
            stageSize,
            vm,
            isRtl,
            isVisible,
            onActivateColorPicker,
            onOpenConnectionModal,
            onOpenSoundRecorder,
            updateToolboxState,
            onActivateCustomProcedures,
            onRequestCloseExtensionLibrary,
            onRequestCloseCustomProcedures,
            toolboxXML,
            updateMetrics: updateMetricsProp,
            useCatBlocks,
            workspaceMetrics,
            ...props
        } = this.props;
        const {servoDial, servoDialClickFeedback} = this.state;
        /* eslint-enable no-unused-vars */
        return (
            <React.Fragment>
                <DroppableBlocks
                    componentRef={this.setBlocks}
                    onDrop={this.handleDrop}
                    {...props}
                />
                {servoDialClickFeedback ? (
                    <div
                        className={servoDialStyles.clickFeedback}
                        style={{
                            top: `${servoDialClickFeedback.top}px`,
                            left: `${servoDialClickFeedback.left}px`,
                            width: `${servoDialClickFeedback.width}px`,
                            height: `${servoDialClickFeedback.height}px`
                        }}
                    />
                ) : null}
                {servoDial ? (
                    <div
                        className={servoDialStyles.popover}
                        onMouseDown={this.handleServoDialPopoverPointerDown}
                        onTouchStart={this.handleServoDialPopoverPointerDown}
                        ref={node => {
                            this.servoDialPopoverRef = node;
                        }}
                        style={{
                            top: `${servoDial.top}px`,
                            left: `${servoDial.left}px`
                        }}
                    >
                        <div className={servoDialStyles.header}>
                            <div className={servoDialStyles.title}>서보 각도</div>
                            <button
                                className={servoDialStyles.closeButton}
                                onClick={this.closeServoDialPopover}
                                type="button"
                            >
                                x
                            </button>
                        </div>
                        <Dial
                            cropBottom
                            direction={90 - servoDial.angle}
                            maxDirection={90}
                            minDirection={-90}
                            onChange={this.handleServoDialChange}
                            radius={44}
                        />
                        <div className={servoDialStyles.angleValue}>{servoDial.angle}°</div>
                        <button
                            className={servoDialStyles.directInputButton}
                            onClick={this.handleServoDialDirectInput}
                            type="button"
                        >
                            직접 입력
                        </button>
                    </div>
                ) : null}
                {this.state.prompt ? (
                    <Prompt
                        defaultValue={this.state.prompt.defaultValue}
                        isStage={vm.runtime.getEditingTarget().isStage}
                        showListMessage={this.state.prompt.varType === this.ScratchBlocks.LIST_VARIABLE_TYPE}
                        label={this.state.prompt.message}
                        showCloudOption={this.state.prompt.showCloudOption}
                        showVariableOptions={this.state.prompt.showVariableOptions}
                        title={this.state.prompt.title}
                        vm={vm}
                        onCancel={this.handlePromptClose}
                        onOk={this.handlePromptCallback}
                    />
                ) : null}
                {this.state.exportImage ? (
                    <BlockExportModal
                        format={this.state.exportImage.format}
                        onCancel={this.handleExportImageClose}
                        onChangeFormat={this.handleExportImageFormatChange}
                        onExport={this.handleExportImageConfirm}
                    />
                ) : null}
                {extensionLibraryVisible ? (
                    <ExtensionLibrary
                        vm={vm}
                        onCategorySelected={this.handleCategorySelected}
                        onRequestClose={onRequestCloseExtensionLibrary}
                    />
                ) : null}
                {customProceduresVisible ? (
                    <CustomProcedures
                        options={{
                            media: options.media
                        }}
                        onRequestClose={this.handleCustomProceduresClose}
                    />
                ) : null}
            </React.Fragment>
        );
    }
}

Blocks.propTypes = {
    anyModalVisible: PropTypes.bool,
    canUseCloud: PropTypes.bool,
    customProceduresVisible: PropTypes.bool,
    extensionLibraryVisible: PropTypes.bool,
    isRtl: PropTypes.bool,
    isVisible: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    messages: PropTypes.objectOf(PropTypes.string),
    onActivateColorPicker: PropTypes.func,
    onActivateCustomProcedures: PropTypes.func,
    onOpenConnectionModal: PropTypes.func,
    onOpenSoundRecorder: PropTypes.func,
    onRequestCloseCustomProcedures: PropTypes.func,
    onRequestCloseExtensionLibrary: PropTypes.func,
    options: PropTypes.shape({
        media: PropTypes.string,
        zoom: PropTypes.shape({
            controls: PropTypes.bool,
            wheel: PropTypes.bool,
            startScale: PropTypes.number
        }),
        comments: PropTypes.bool,
        collapse: PropTypes.bool
    }),
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    theme: PropTypes.oneOf(Object.keys(themeMap)),
    toolboxXML: PropTypes.string,
    updateMetrics: PropTypes.func,
    updateToolboxState: PropTypes.func,
    useCatBlocks: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired,
    workspaceMetrics: PropTypes.shape({
        targets: PropTypes.objectOf(PropTypes.object)
    }),
    generated_code: PropTypes.string

};

Blocks.defaultOptions = {
    zoom: {
        controls: true,
        wheel: true,
        startScale: BLOCKS_DEFAULT_SCALE
    },
    grid: {
        spacing: 40,
        length: 2,
        colour: '#ddd'
    },
    comments: true,
    collapse: false,
    sounds: false
};

Blocks.defaultProps = {
    isVisible: true,
    options: Blocks.defaultOptions,
    theme: DEFAULT_THEME
};

const mapStateToProps = state => ({
    anyModalVisible: (
        Object.keys(state.scratchGui.modals).some(key => state.scratchGui.modals[key]) ||
        state.scratchGui.mode.isFullScreen
    ),
    extensionLibraryVisible: state.scratchGui.modals.extensionLibrary,
    isRtl: state.locales.isRtl,
    locale: state.locales.locale,
    messages: state.locales.messages,
    toolboxXML: state.scratchGui.toolbox.toolboxXML,
    customProceduresVisible: state.scratchGui.customProcedures.active,
    workspaceMetrics: state.scratchGui.workspaceMetrics,
    useCatBlocks: isTimeTravel2020(state),
    changeMonskitGeneratedCodes: state.scratchGui.MonskitGeneratedCodes,
    servoLiveEnabled: state.scratchGui.servoLive.enabled,
    servoLiveConnected: state.scratchGui.servoLive.connected

});

const mapDispatchToProps = dispatch => ({
    onActivateColorPicker: callback => dispatch(activateColorPicker(callback)),
    onActivateCustomProcedures: (data, callback) => dispatch(activateCustomProcedures(data, callback)),
    onOpenConnectionModal: id => {
        dispatch(setConnectionModalExtensionId(id));
        dispatch(openConnectionModal());
    },
    onOpenSoundRecorder: () => {
        dispatch(activateTab(SOUNDS_TAB_INDEX));
        dispatch(openSoundRecorder());
    },
    onRequestCloseExtensionLibrary: () => {
        dispatch(closeExtensionLibrary());
    },
    onRequestCloseCustomProcedures: data => {
        dispatch(deactivateCustomProcedures(data));
    },
    updateToolboxState: toolboxXML => {
        dispatch(updateToolbox(toolboxXML));
    },
    updateMetrics: metrics => {
        dispatch(updateMetrics(metrics));
    },
    onchangeMonskitGeneratedCodes: codes => dispatch(changeMonskitGeneratedCodes(codes))

});

export default errorBoundaryHOC('Blocks')(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(Blocks)
);
